import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { AlignmentForm } from './ArcgisCivil3dAlignmentSettingForm';
import { useSearchAlignmentStyles } from '../../../hooks/api/use-alignment-styles.hook';
import { useSearchAutodeskLayers } from '../../../hooks/api/use-autodesk-layers.hook';
import { useGetSettingConfiguration } from '../../../hooks/api/use-arcgis-civil3d-setting-configurations.hook';
import { useSearchArcgisFeatureClasses } from '../../../hooks/api/use-arcgis-feature-classes.hook';
import { AutodeskLayerRelationship, WithLayer } from '../../../../../types/models/autodesk-layer.model';
import { useCreateAlignmentSetting } from '../../../hooks/api/use-arcgis-civil3d-alignment-settings.hook';
import { civil3dFeatureTypesByCategory, Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';

export const AddAlignmentSetting = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();

  const { loading: loadingAlignmentStyles, response: alignmentStylesResponse } = useSearchAlignmentStyles({
    limit: 99999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingAutodeskLayers, response: autodeskLayersResponse } = useSearchAutodeskLayers({
    limit: 99999,
    include: [AutodeskLayerRelationship.LAYER],
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingSettingConfiguration, settingConfiguration } = useGetSettingConfiguration({
    settingConfigurationId: Number(configurationSettingId),
  });
  const { loading: loadingFeatureClasses, response: featureClassesResponse } = useSearchArcgisFeatureClasses({
    limit: 99999,
  });

  const [createAlignmentSetting, { loading: creatingAlignment, error: createError }] = useCreateAlignmentSetting();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AlignmentForm>({
    resolver: classValidatorResolver(AlignmentForm),
    defaultValues: { configuration: settingConfiguration },
  });

  const handleCreateAlignment = async ({
    alignmentStyle,
    autodeskLayer,
    configuration,
    featureClass,
    ...values
  }: AlignmentForm) => {
    await createAlignmentSetting({
      ...values,
      alignmentStyleId: alignmentStyle?.id,
      autodeskLayerId: autodeskLayer?.id,
      configurationId: configuration?.id,
      arcgisFeatureClassId: featureClass.id,
    });
  };

  const onSubmit: SubmitHandler<AlignmentForm> = async (values) => {
    await handleCreateAlignment(values);

    navigate('..');
  };

  const renderContent = () => (
    <div>
      <Controller
        control={control}
        name="configuration"
        render={({ field }) => (
          <Autocomplete
            {...register('configuration')}
            loading={loadingSettingConfiguration}
            sx={{ mt: '1rem' }}
            disabled
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={[settingConfiguration]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Civil 3D Setting Configuration"
                error={Boolean(errors.configuration)}
                helperText={errors.configuration?.message}
              />
            )}
          />
        )}
      />
      <TextField
        {...register('name')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingAlignment}
        error={Boolean(errors.name)}
        helperText={errors.name?.message}
        label="Name"
        margin="dense"
        size="small"
        fullWidth
      />
      <TextField
        {...register('query')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingAlignment}
        error={Boolean(errors.query)}
        helperText={errors.query?.message}
        label="Query"
        margin="dense"
        size="small"
        multiline
        rows={4}
        fullWidth
      />
      <TextField
        {...register('priority', { valueAsNumber: true })}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingAlignment}
        error={Boolean(errors.priority)}
        helperText={errors.priority?.message}
        label="Priority"
        margin="dense"
        size="small"
        type="number"
        fullWidth
      />
      <Controller
        control={control}
        name="alignmentStyle"
        render={({ field }) => (
          <Autocomplete
            {...register('alignmentStyle')}
            loading={loadingAlignmentStyles}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(alignmentStyle) => alignmentStyle?.name ?? ''}
            options={alignmentStylesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Style"
                error={Boolean(errors.alignmentStyle)}
                helperText={errors.alignmentStyle?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="autodeskLayer"
        render={({ field }) => (
          <Autocomplete
            loading={loadingAutodeskLayers}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOption) => field.onChange(selectedOption)}
            onBlur={field.onBlur}
            value={field.value ?? null}
            getOptionLabel={(autodeskLayer) => (autodeskLayer as WithLayer)?.layer?.name ?? ''}
            options={autodeskLayersResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Autodesk Layer"
                error={Boolean(errors.autodeskLayer)}
                helperText={errors.autodeskLayer?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="featureClass"
        render={({ field }) => (
          <Autocomplete
            {...register('featureClass')}
            loading={loadingFeatureClasses}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(configuration) => configuration?.name ?? ''}
            options={featureClassesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ArcGIS Feature Class"
                error={Boolean(errors.featureClass)}
                helperText={errors.featureClass?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="civil3dFeatureType"
        render={({ field }) => (
          <Autocomplete
            {...register('civil3dFeatureType')}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(featureType) => featureType}
            options={civil3dFeatureTypesByCategory[Civil3dFeatureType.ALIGNMENT]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Civil 3D Feature Type"
                error={Boolean(errors.civil3dFeatureType)}
                helperText={errors.civil3dFeatureType?.message}
              />
            )}
          />
        )}
      />
    </div>
  );

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
        <DialogTitle>Add ArcGIS Civil 3D Feature Alignment</DialogTitle>
        <DialogContent>
          {renderContent()}
          {createError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {createError?.response?.data?.message || createError?.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
